import { Checkbox, Flex, Tooltip } from "@mantine/core";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import ScrollableTable from "src/components/shared/ScrollableTable/ScrollableTable";
import { type ISearchResult } from "src/pages/SearchPage/SearchPage.types";
import { useDispatch, useSelector } from "src/redux/hooks";
import { toggleToExport } from "src/redux/slices/searchSlice";
import { StyledTd, StyledTr } from "src/styled";

import { deepClone, MEDLBID } from "../../../utils";
import { SearchResultsWrapper } from "../../pages/Search/Search.styled";

interface Props {
  data: ISearchResult[];
  loading?: boolean;
  isMedical?: boolean;
  isNormalEntries?: boolean;
  additionalOnNavigation?: () => void;
}

const headers = [
  {
    name: "name",
    label: "Logbook",
    style: { width: "40%" },
  },
  {
    name: "date",
    label: "Open Date",
    style: { width: "10%" },
  },
  {
    name: "entry",
    label: "Entry Date",
    style: { width: "10%" },
  },
  {
    name: "blank",
    label: "",
    style: { width: "10%" },
  },
];

const ResultsTable = ({
  data,
  loading,
  isMedical,
  isNormalEntries,
  additionalOnNavigation,
}: Props) => {
  const navigate = useNavigate();
  const { isDarkMode, uppercase } = useSelector(
    state => state.preferencesSlice,
  );
  const toExport = useSelector(state => state.searchSlice.toExport);
  const { collectionId, logbookId } = useParams();
  const dispatch = useDispatch();
  const parsedHeaders = useMemo(() => {
    const parsed: any[] = deepClone(headers);
    if (!isNormalEntries) {
      parsed.splice(1, 0, {
        name: "mention",
        label: "Mention",
        style: { width: "30%" },
      });
    }
    if (isMedical) {
      parsed[0] = {
        ...headers[0],
        name: "patient",
        label: "Patient",
      };
      parsed[1] = {
        ...parsed[1],
        name: "diagnosis",
        label: "Diagnosis",
      };
    }

    return parsed;
  }, [isMedical, isNormalEntries]);

  const getNavigationTarget = useCallback(
    (row: ISearchResult) => {
      const idType = row.weid
        ? "weid"
        : row.meid
          ? "meid"
          : row.partid
            ? "partid"
            : row.lbeid
              ? "lbeid"
              : "";
      if (!idType) return undefined;

      const id = row[idType as keyof ISearchResult];
      const lbid = row.lbid ?? logbookId;
      // Search results (e.g. a ref-number lookup) often don't include the
      // parent logbook's id — fall back to the same lbid-less "verify
      // entries" route ActionsCell.handleView uses elsewhere in the app,
      // which resolves the entry (and its logbook) from idType/id alone.
      const path = lbid
        ? `/logbook/${lbid}/${idType}/${id}`
        : `/summary/verify-entries/${idType}/${id}`;
      return { path };
    },
    [logbookId],
  );

  const handleNavigate = useCallback(
    (row: ISearchResult) => {
      const target = getNavigationTarget(row);
      if (!target) return undefined;

      return () => {
        navigate(target.path);
        if (additionalOnNavigation) additionalOnNavigation();
      };
    },
    [additionalOnNavigation, getNavigationTarget, navigate],
  );

  const handleCheck = useCallback(
    (meid: string) => () => {
      dispatch(toggleToExport(meid));
    },
    [dispatch],
  );

  const renderRow = useCallback(
    ({ row }: { row: ISearchResult }) => (
      <StyledTr
        background={
          isDarkMode ? "transparent" : "var(--mantine-color-cardBackground-0)"
        }
        hover>
        <StyledTd>
          {isMedical ? (
            <p>{row.patient}</p>
          ) : (
            <div>
              {row.lbname}
              {row.notes && (
                <Flex mt={12}>
                  Notes:
                  <Flex direction="column" gap={8} ml={16}>
                    {row.notes.map((note, index) => (
                      <span
                        key={index}
                        style={{
                          textTransform: uppercase ? "uppercase" : "none",
                        }}>
                        {note}
                      </span>
                    ))}
                  </Flex>
                </Flex>
              )}
            </div>
          )}
        </StyledTd>
        {!isNormalEntries && (
          <StyledTd style={{ textTransform: uppercase ? "uppercase" : "none" }}>
            {row.mention ? row.mention : row.diagnosis}
          </StyledTd>
        )}
        <StyledTd center>
          {dayjs(row.datecreated).format("D MMM YYYY")}
        </StyledTd>
        <StyledTd center>
          {row.entrydate ? dayjs(row.entrydate).format("D MMM YYYY") : ""}
        </StyledTd>
        <StyledTd center>
          <Flex align="center" gap="sm" justify="center">
            {collectionId === MEDLBID && (
              <Checkbox
                checked={toExport.includes(row.meid || row.lbeid)}
                onChange={handleCheck(row.meid || row.lbeid)}
              />
            )}
            <Tooltip
              disabled={!!getNavigationTarget(row)}
              label="Unable to determine this entry's logbook"
              withArrow>
              <Button
                onClick={handleNavigate(row)}
                disabled={!getNavigationTarget(row)}>
                View
              </Button>
            </Tooltip>
          </Flex>
        </StyledTd>
      </StyledTr>
    ),
    [
      collectionId,
      getNavigationTarget,
      handleCheck,
      handleNavigate,
      isDarkMode,
      isMedical,
      isNormalEntries,
      toExport,
      uppercase,
    ],
  );

  return (
    <SearchResultsWrapper>
      <ScrollableTable
        style={{ padding: 20 }}
        data={data}
        headers={parsedHeaders}
        renderRow={renderRow}
        loading={loading}
        sortable
      />
    </SearchResultsWrapper>
  );
};

export default ResultsTable;
